import { action, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import type { RepositoryVehicles } from "../../domain/repositories/repository-vehicles";
import { type UUID } from "../../utils/uuid";
import { ListCommandButtons, type ListCommandButtonsProps } from "./list-command-buttons";
import { FormUpdateVehicle } from "../molecules/form-update-vehicle";
import { type ErrorReport } from "../../domain/services/service-errors";
import { defaultPageSize } from "../../utils/constants";
import type { ResponseError } from "../../api";

export type ListCommandButtonsVehiclesProps = Omit<
    ListCommandButtonsProps<RepositoryVehicles>,
    "list" | "createForm" | "listState"
>;

import { store } from "../../store";

@observer
export class ListCommandButtonsVehicles extends React.Component<ListCommandButtonsVehiclesProps> {
    private undeletableVehicles: string[] = [];

    constructor(props: ListCommandButtonsVehiclesProps) {
        super(props);
        makeObservable(this);
    }

    /** Tries to delete all vehicles and reports an error on
        encounter with a vehicle linked to events.
    */
    @action.bound private async deleteVehicles(ids: UUID[]): Promise<void> {
        await Promise.all(ids.map((id) => this.deleteSingleVehicle(id)));

        // This reload is necessary to reload all queries that want to display "all" entities.
        await store.repositoryVehicles.waitForIdle();
        await store.repositoryVehicles.reloadQuery({ pageSize: defaultPageSize });

        // If we successfully deleted all vehicles return early
        if (this.undeletableVehicles.length == 0) {
            return;
        }

        // Report error that lists all undeletable vehicles
        const vehicleList = this.undeletableVehicles.map((vehicle) => <li>{vehicle}</li>);
        const errorReport = {
            title: "component.errorBar.vehicle.unableToDeleteVehicle.title",
            description: "component.errorBar.vehicle.unableToDeleteVehicle.description",
            html: <ul>{vehicleList}</ul>,
        } as ErrorReport;
        void store.serviceErrors.reportError(errorReport);
        this.undeletableVehicles = [];
    }

    /** Tries to delete a single vehicle. If we receive a validation error (422)
        we mark this vehicle as undeletable. */
    private async deleteSingleVehicle(id: UUID): Promise<void> {
        try {
            await store.vehiclesApi.vehiclesDeleteVehicle({ vehicleId: id });
            return;
        } catch (error) {
            const responseError = error as ResponseError;
            const response = responseError.response;

            // Keep track of vehicles that produce a validation error
            if (response.status === 422) {
                const vehicle = store.repositoryVehicles.byId(id);
                this.undeletableVehicles.push(store.i18n.formatVehicle(vehicle!));
                return;
            } else {
                // We still have to report other errors (e.g., internal server error)
                store.serviceErrors.reportResponse(response);
            }
        }
    }

    public render(): JSX.Element {
        return (
            <ListCommandButtons
                updateForm={FormUpdateVehicle}
                listState={store.serviceListStates.vehicles}
                createDialogTitle={store.i18n.t("listVehicles.createDialog.title")}
                updateDialogTitle={store.i18n.t("listVehicles.updateDialog.title")}
                {...this.props}
                onDelete={this.deleteVehicles}
                dialogMaxWidth="1200px"
            />
        );
    }
}
